import { Input, Modal, message, Tabs } from 'antd';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { whitelistStore } from '@/stores';

const { TextArea } = Input;

interface AddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ValidationResult {
  valid: boolean;
  userIds: string[];
  errors: string[];
}

// 校验批量输入的用户ID
function validateBatchInput(input: string): ValidationResult {
  const errors: string[] = [];
  const userIds: string[] = [];

  if (!input.trim()) {
    return { valid: false, userIds: [], errors: ['请输入用户ID'] };
  }

  const rawIds = input.split(',');

  rawIds.forEach((id, index) => {
    const trimmedId = id.trim();
    const position = index + 1;

    // 检查是否为空
    if (!trimmedId) {
      errors.push(`第 ${position} 个位置的用户ID为空`);
      return;
    }

    // 检查是否包含空格
    if (/\s/.test(trimmedId)) {
      errors.push(`第 ${position} 个用户ID "${trimmedId}" 包含空格`);
      return;
    }

    // 检查是否包含非法字符（只允许字母、数字、下划线、连字符）
    if (!/^[\w-]+$/.test(trimmedId)) {
      errors.push(`第 ${position} 个用户ID "${trimmedId}" 包含非法字符`);
      return;
    }

    // 检查是否重复
    if (userIds.includes(trimmedId)) {
      errors.push(`用户ID "${trimmedId}" 重复`);
      return;
    }

    userIds.push(trimmedId);
  });

  return {
    valid: errors.length === 0 && userIds.length > 0,
    userIds,
    errors,
  };
}

export function AddDialog({ open, onOpenChange }: AddDialogProps) {
  const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');
  const [userId, setUserId] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const resetForm = () => {
    setUserId('');
    setBatchInput('');
    setValidationErrors([]);
    setActiveTab('single');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  // 单个添加
  const handleSingleSubmit = async () => {
    const trimmedId = userId.trim();
    if (!trimmedId) {
      return;
    }

    // 校验单个用户ID
    if (/\s/.test(trimmedId)) {
      setValidationErrors(['用户ID不能包含空格']);
      return;
    }

    if (!/^[\w-]+$/.test(trimmedId)) {
      setValidationErrors(['用户ID只能包含字母、数字、下划线和连字符']);
      return;
    }

    setLoading(true);
    setValidationErrors([]);
    try {
      await whitelistStore.addUserToWhitelist({ user_id: trimmedId });
      message.success('添加成功');
      handleClose();
    } catch (error) {
      console.error('添加失败:', error);
      message.error('添加失败');
    } finally {
      setLoading(false);
    }
  };

  // 批量添加
  const handleBatchSubmit = async () => {
    const result = validateBatchInput(batchInput);

    if (!result.valid) {
      setValidationErrors(result.errors);
      return;
    }

    setLoading(true);
    setValidationErrors([]);

    let successCount = 0;
    let failCount = 0;
    const failedIds: string[] = [];

    try {
      for (const id of result.userIds) {
        try {
          await whitelistStore.addUserToWhitelist({ user_id: id });
          successCount++;
        } catch (error) {
          failCount++;
          failedIds.push(id);
        }
      }

      if (failCount === 0) {
        message.success(`成功添加 ${successCount} 个用户`);
        handleClose();
      } else {
        message.warning(`成功 ${successCount} 个，失败 ${failCount} 个`);
        setValidationErrors([`以下用户ID添加失败：${failedIds.join(', ')}`]);
        // 刷新列表
        whitelistStore.fetchWhitelist();
      }
    } catch (error) {
      console.error('批量添加失败:', error);
      message.error('批量添加失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (activeTab === 'single') {
      handleSingleSubmit();
    } else {
      handleBatchSubmit();
    }
  };

  const isSubmitDisabled = () => {
    if (loading) return true;
    if (activeTab === 'single') {
      return !userId.trim();
    }
    return !batchInput.trim();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="添加用户到白名单"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {loading ? '添加中...' : '添加'}
          </Button>
        </div>
      }
      centered
      width={520}
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
        },
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as 'single' | 'batch');
          setValidationErrors([]);
        }}
        items={[
          {
            key: 'single',
            label: '单个添加',
            children: (
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  请输入用户ID以添加到权限白名单
                </p>
                <div className="space-y-2">
                  <Label htmlFor="userId">用户ID *</Label>
                  <Input
                    id="userId"
                    value={userId}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      setValidationErrors([]);
                    }}
                    placeholder="请输入用户ID"
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'batch',
            label: '批量导入',
            children: (
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  输入多个用户ID，使用英文逗号{' '}
                  <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">
                    ,
                  </code>{' '}
                  分隔
                </p>
                <div className="space-y-2">
                  <Label htmlFor="batchInput">用户ID列表 *</Label>
                  <TextArea
                    id="batchInput"
                    value={batchInput}
                    onChange={(e) => {
                      setBatchInput(e.target.value);
                      setValidationErrors([]);
                    }}
                    placeholder="例如：user001,user002,user003"
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    用户ID只能包含字母、数字、下划线和连字符，不能包含空格
                  </p>
                </div>
              </div>
            ),
          },
        ]}
      />

      {/* 校验错误提示 */}
      {validationErrors.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm font-medium text-red-800 mb-1">
            输入校验失败：
          </p>
          <ul className="text-sm text-red-600 list-disc list-inside space-y-0.5">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
